import React from 'react';
import { motion } from 'framer-motion';
interface SyllableBlocksProps {
  word: string;
  syllables: string[];
  types: ('open' | 'closed')[];
  highlight?: number;
  label?: string;
}
/**
 * SyllableBlocks - Word syllable breakdown visual
 *
 * Shows word broken into syllables with open/closed indication.
 * Used for SPR (Spelling Regels) module sound group questions.
 */
export function SyllableBlocks({
  word,
  syllables,
  types,
  highlight,
  label
}: SyllableBlocksProps) {
  // Find vowels in each syllable
  const getVowels = (syllable: string): number[] => {
    const vowels = ['a', 'e', 'i', 'o', 'u', 'y'];
    const positions: number[] = [];
    syllable.split('').forEach((char, index) => {
      if (vowels.includes(char.toLowerCase())) {
        positions.push(index);
      }
    });
    return positions;
  };
  return <div className="w-full max-w-3xl mx-auto" data-id="element-5866">
      <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 md:p-8 border-2 border-green-200 shadow-lg" data-id="element-5867">
        {/* Header */}
        <div className="mb-6 text-center" data-id="element-5868">
          <div className="inline-flex items-center gap-3 bg-white/90 rounded-xl px-5 py-3 border border-green-200 shadow-sm" data-id="element-5869">
            <span className="text-2xl" data-id="element-5870">🔤</span>
            <div className="text-left" data-id="element-5871">
              <div className="text-sm font-bold text-green-900" data-id="element-5872">
                {label || 'Lettergrepen Verdeling'}
              </div>
              <div className="text-xs text-green-600 font-semibold" data-id="element-5873">
                Open en Gesloten Lettergrepen
              </div>
            </div>
          </div>
        </div>

        {/* Complete Word */}
        <div className="mb-6 text-center" data-id="element-5874">
          <div className="inline-flex flex-col items-center gap-2 bg-white/80 rounded-xl px-6 py-4 border border-slate-200 shadow-sm" data-id="element-5875">
            <div className="text-xs font-bold text-slate-500 uppercase tracking-wide" data-id="element-5876">
              Heel Woord
            </div>
            <div className="text-3xl font-black text-slate-800" data-id="element-5877">{word}</div>
          </div>
        </div>

        {/* Syllable Blocks */}
        <div className="mb-6" data-id="element-5878">
          <div className="flex justify-center items-center gap-3 flex-wrap" data-id="element-5879">
            {syllables.map((syllable, index) => {
            const type = types[index];
            const isHighlighted = highlight === index;
            const vowelPositions = getVowels(syllable);
            return <motion.div key={index} initial={{
              scale: 0,
              y: 20
            }} animate={{
              scale: 1,
              y: 0
            }} transition={{
              delay: index * 0.15,
              type: 'spring',
              stiffness: 300
            }} className="flex flex-col items-center" data-id="element-5880">

                  {/* Syllable Block */}
                  <div className={`
                    relative px-6 py-4 rounded-2xl border-4 shadow-xl
                    transition-all duration-300
                    ${type === 'open' ? 'bg-gradient-to-br from-green-400 to-emerald-500 border-green-600' : 'bg-gradient-to-br from-red-400 to-rose-500 border-red-600'}
                    ${isHighlighted ? 'ring-4 ring-yellow-300 scale-110' : ''}
                  `} data-id="element-5881">

                    {/* Letters */}
                    <div className="flex gap-1" data-id="element-5882">
                      {syllable.split('').map((char, charIndex) => {
                    const isVowel = vowelPositions.includes(charIndex);
                    return <span key={charIndex} className={`
                              text-2xl md:text-3xl font-black
                              ${isVowel ? 'text-white drop-shadow-lg' : 'text-white/90'}
                            `} data-id="element-5883">

                            {char}
                          </span>;
                  })}
                    </div>

                    {/* Type indicator */}
                    <div className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white border-2 border-current flex items-center justify-center shadow-md" data-id="element-5884">
                      <span className="text-xs font-bold" data-id="element-5885">
                        {type === 'open' ? '○' : '●'}
                      </span>
                    </div>
                  </div>

                  {/* Label */}
                  <div className={`
                    mt-2 px-3 py-1 rounded-lg text-xs font-bold
                    ${type === 'open' ? 'bg-green-100 text-green-800 border border-green-200' : 'bg-red-100 text-red-800 border border-red-200'}
                  `} data-id="element-5886">

                    {type === 'open' ? 'Open (lange klank)' : 'Gesloten (korte klank)'}
                  </div>
                </motion.div>;
          })}
          </div>
        </div>

        {/* Educational Explanations */}
        <div className="space-y-4" data-id="element-5887">
          {/* What do you see? */}
          <div className="bg-white/80 rounded-xl p-5 border border-blue-100 shadow-sm" data-id="element-5888">
            <div className="flex items-start gap-3" data-id="element-5889">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0" data-id="element-5890">
                <span className="text-blue-600 text-lg" data-id="element-5891">👀</span>
              </div>
              <div data-id="element-5892">
                <h4 className="text-sm font-bold text-blue-900 mb-2" data-id="element-5893">
                  Wat zie je?
                </h4>
                <div className="text-sm text-slate-700 leading-relaxed space-y-2" data-id="element-5894">
                  <p data-id="element-5895">
                    Het woord <strong data-id="element-5896">{word}</strong> bestaat uit{' '}
                    <strong data-id="element-5897">{syllables.length}</strong> lettergreep
                    {syllables.length > 1 ? 'en' : ''}:{' '}
                    {syllables.map((s, i) => <span key={i} data-id="element-5898">
                        <strong className={types[i] === 'open' ? 'text-green-700' : 'text-red-700'} data-id="element-5899">

                          {s}
                        </strong>
                        {i < syllables.length - 1 ? ' + ' : ''}
                      </span>)}
                  </p>
                  <div className="pt-2 border-t border-blue-100 space-y-1" data-id="element-5900">
                    <p className="flex items-center gap-2" data-id="element-5901">
                      <span className="w-4 h-4 rounded bg-green-500" data-id="element-5902"></span>
                      <span data-id="element-5903">
                        <strong data-id="element-5904">Groen (○):</strong> Open lettergreep = lange
                        klank (één medeklinker na de klinker)
                      </span>
                    </p>
                    <p className="flex items-center gap-2" data-id="element-5905">
                      <span className="w-4 h-4 rounded bg-red-500" data-id="element-5906"></span>
                      <span data-id="element-5907">
                        <strong data-id="element-5908">Rood (●):</strong> Gesloten lettergreep = korte
                        klank (twee medeklinkers na de klinker)
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Open vs Closed explanation */}
          <div className="bg-emerald-50/80 rounded-xl p-5 border border-emerald-200 shadow-sm" data-id="element-5909">
            <div className="flex items-start gap-3" data-id="element-5910">
              <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-5911">
                <span className="text-emerald-600 text-lg" data-id="element-5912">📖</span>
              </div>
              <div data-id="element-5913">
                <h4 className="text-sm font-bold text-emerald-900 mb-2" data-id="element-5914">
                  Open vs Gesloten Lettergrepen
                </h4>
                <div className="text-sm text-slate-700 leading-relaxed space-y-3" data-id="element-5915">
                  <div className="bg-green-50 p-3 rounded-lg border border-green-200" data-id="element-5916">
                    <p className="font-semibold text-green-800 mb-1" data-id="element-5917">
                      Open lettergreep (○):
                    </p>
                    <p data-id="element-5918">
                      Eindigt op een <strong data-id="element-5919">klinker</strong> (a, e, i, o, u).
                      De klinker klinkt <strong data-id="element-5920">lang</strong>.
                    </p>
                    <p className="text-xs mt-1 text-green-700" data-id="element-5921">
                      Voorbeeld: <strong data-id="element-5922">ja-ger</strong> (ja = open, lange 'a')
                    </p>
                  </div>
                  <div className="bg-red-50 p-3 rounded-lg border border-red-200" data-id="element-5923">
                    <p className="font-semibold text-red-800 mb-1" data-id="element-5924">
                      Gesloten lettergreep (●):
                    </p>
                    <p data-id="element-5925">
                      Eindigt op een <strong data-id="element-5926">medeklinker</strong>. De klinker
                      klinkt <strong data-id="element-5927">kort</strong>.
                    </p>
                    <p className="text-xs mt-1 text-red-700" data-id="element-5928">
                      Voorbeeld: <strong data-id="element-5929">bak-ker</strong> (bak = gesloten, korte
                      'a')
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Spelling rule */}
          <div className="bg-indigo-50/80 rounded-xl p-5 border border-indigo-200 shadow-sm" data-id="element-5930">
            <div className="flex items-start gap-3" data-id="element-5931">
              <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0" data-id="element-5932">
                <span className="text-indigo-600 text-lg" data-id="element-5933">✍️</span>
              </div>
              <div data-id="element-5934">
                <h4 className="text-sm font-bold text-indigo-900 mb-2" data-id="element-5935">
                  Spellingregel
                </h4>
                <div className="text-sm text-slate-700 leading-relaxed space-y-2" data-id="element-5936">
                  <p data-id="element-5937">
                    <strong data-id="element-5938">Korte klank (gesloten):</strong> Schrijf meestal{' '}
                    <strong data-id="element-5939">twee medeklinkers</strong> na de klinker.
                  </p>
                  <p className="text-xs text-indigo-700 bg-indigo-50 p-2 rounded border border-indigo-200" data-id="element-5940">
                    Voorbeeld: bak-ker (korte 'a' → dubbele k), zit-ten (korte
                    'i' → dubbele t)
                  </p>
                  <p className="pt-2 border-t border-indigo-200" data-id="element-5941">
                    <strong data-id="element-5942">Lange klank (open):</strong> Schrijf meestal{' '}
                    <strong data-id="element-5943">één medeklinker</strong> na de klinker.
                  </p>
                  <p className="text-xs text-indigo-700 bg-indigo-50 p-2 rounded border border-indigo-200" data-id="element-5944">
                    Voorbeeld: ja-ger (lange 'a' → enkele g), bo-men (lange 'o'
                    → enkele m)
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Tip */}
          <div className="bg-amber-50/80 rounded-xl p-5 border border-amber-200 shadow-sm" data-id="element-5945">
            <div className="flex items-start gap-3" data-id="element-5946">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0" data-id="element-5947">
                <span className="text-amber-600 text-lg" data-id="element-5948">💡</span>
              </div>
              <div data-id="element-5949">
                <h4 className="text-sm font-bold text-amber-900 mb-2" data-id="element-5950">
                  Handige tip
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed" data-id="element-5951">
                  Spreek het woord hardop uit! Hoor je een{' '}
                  <strong data-id="element-5952">korte</strong> klinker? Dan is de lettergreep meestal{' '}
                  <strong data-id="element-5953">gesloten</strong> en schrijf je{' '}
                  <strong data-id="element-5954">twee medeklinkers</strong>. Hoor je een{' '}
                  <strong data-id="element-5955">lange</strong> klinker? Dan is de lettergreep{' '}
                  <strong data-id="element-5956">open</strong> en schrijf je{' '}
                  <strong data-id="element-5957">één medeklinker</strong>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}